import { AnimatePresence, motion } from 'framer-motion';
import React, { useState, useMemo, useCallback } from 'react';
import ReactConfetti from 'react-confetti';
import { IoChatbubbleEllipses, IoArrowBack } from 'react-icons/io5';

import AnimatedButton from './components/AnimatedButton';
import BackgroundDecor from './components/BackgroundDecor';
import ProgressDots from './components/ProgressDots';
import StepCard from './components/StepCard';
import TypewriterText from './components/TypewriterText';
import { appConfig } from './data/config';
import { trackYes } from './utils/tracking';
import TrackOnMount from './components/TrackOnMount';
import { rejectSteps } from './data/rejectSteps';
import { introSteps } from './data/steps';

// ─── Modes ────────────────────────────────────────────────────────────────────
const MODE = {
  INTRO: 'intro',
  CONFESSION: 'confession',
  REJECT_LOOP: 'reject-loop',
  SUCCESS: 'success',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getQueryParam(key) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

function useConfig() {
  return useMemo(() => {
    const to = getQueryParam('to');
    const from = getQueryParam('from');
    return {
      ...appConfig,
      targetName: to || appConfig.targetName,
      senderName: from || appConfig.senderName,
    };
  }, []);
}

// ─── Screen: Opening ─────────────────────────────────────────────────────────
function OpeningScreen({ config, onStart }) {
  return (
    <StepCard>
      <motion.div
        className="mb-6 text-5xl"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
        aria-hidden="true"
      >
        ✉️
      </motion.div>

      <h1 className="font-display text-3xl font-bold text-rose-600 leading-tight mb-3 text-shadow-soft">
        Haii {config.targetName},
      </h1>
      <p className="font-body text-rose-400/80 text-base mb-8 leading-relaxed">
        I made something for you, so please read this okayyy
      </p>

      <AnimatedButton onClick={onStart} variant="primary" size="lg">
        Okay, what is it?
      </AnimatedButton>
    </StepCard>
  );
}

// ─── Screen: Intro Step ───────────────────────────────────────────────────────
function IntroStepScreen({ step, stepIndex, total, onNext, onBack }) {
  const [typed, setTyped] = useState(false);

  // Reset typed state on step change
  React.useEffect(() => {
    setTyped(false);
  }, [stepIndex]);

  return (
    <StepCard>
      {/* back button for intro steps */}
      <motion.div className="absolute left-4 top-4">
        <button
          onClick={onBack}
          aria-label="Back"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full text-rose-600 bg-white/70 shadow-sm hover:bg-white"
        >
          <IoArrowBack />
        </button>
      </motion.div>
      <div className="mb-6 text-4xl" aria-hidden="true">
        {['✨', '💭', '🌸', '💛', '🌷', '🤍'][stepIndex % 6]}
      </div>

      <h2 className="font-display italic text-2xl font-semibold text-rose-700 leading-snug mb-3 min-h-[3rem]">
        <TypewriterText
          text={step.message}
          speed={38}
          onDone={() => setTyped(true)}
        />
      </h2>

      {step.sub && typed && (
        <motion.p
          className="font-body text-rose-400/80 text-sm mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {step.sub}
        </motion.p>
      )}

      {!step.sub && typed && <div className="mb-8" />}

      <div className="mt-auto flex flex-col items-center gap-4">
        {typed && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.35 }}
          >
            <AnimatedButton onClick={onNext} variant="primary" size="md">
              Next
            </AnimatedButton>
          </motion.div>
        )}
        <ProgressDots total={total} current={stepIndex} />
      </div>
    </StepCard>
  );
}

// ─── Screen: Confession ───────────────────────────────────────────────────────
function ConfessionScreen({ config, onYes, onNo }) {
  return (
    <StepCard className="py-12">
      <motion.div
        className="text-5xl mb-5"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
        aria-hidden="true"
      >
        💖
      </motion.div>

      <h2 className="font-display text-3xl font-bold text-rose-600 leading-tight mb-3 text-shadow-soft">
        Will you be
        <br />
        mine?
      </h2>
      <p className="font-body text-rose-400/70 text-sm mb-10 leading-relaxed">
        From {config.senderName}, with everything.
      </p>

      <div className="flex flex-col gap-3 items-center w-full">
        <AnimatedButton onClick={onYes} variant="primary" size="lg" className="w-full max-w-xs">
          Yes 💖
        </AnimatedButton>
        <AnimatedButton onClick={onNo} variant="secondary" size="md" className="w-full max-w-xs">
          No 😶
        </AnimatedButton>
      </div>
    </StepCard>
  );
}

// ─── Screen: Reject Loop ──────────────────────────────────────────────────────
function RejectLoopScreen({ step, rejectIndex, onYes, onNo }) {
  const [typed, setTyped] = useState(false);

  React.useEffect(() => {
    setTyped(false);
  }, [rejectIndex]);

  // On later steps make "Tidak" smaller and shift position
  const noScale = Math.max(0.7, 1 - rejectIndex * 0.06);
  const yesScale = Math.min(1.2, 1 + rejectIndex * 0.05);

  return (
    <StepCard className="py-12">
      <motion.div
        className="text-5xl mb-5"
        animate={{ rotate: [0, -6, 6, 0] }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        aria-hidden="true"
      >
        {/* reduced icons: show subtle emoji only for early steps */}
        {(() => {
          const icons = ['🥺', '😔', '😩'];
          const ic = icons[rejectIndex] || '';
          return ic;
        })()}
      </motion.div>

      <h2 className="font-display italic text-2xl font-bold text-rose-600 leading-snug mb-3 min-h-[2.5rem]">
        <TypewriterText
          text={step.message}
          speed={42}
          onDone={() => setTyped(true)}
        />
      </h2>

      {step.sub && typed && (
        <motion.p
          className="font-body text-rose-400/80 text-sm mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {step.sub}
        </motion.p>
      )}
      {!step.sub && typed && <div className="mb-8" />}

      {typed && (
        <motion.div
          className="flex flex-col gap-3 items-center w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.35 }}
        >
          <AnimatedButton
            onClick={onYes}
            variant="primary"
            size="lg"
            className="w-full max-w-xs"
            style={{ transform: `scale(${yesScale})` }}
          >
            Yes 💖
          </AnimatedButton>
          <motion.div
            animate={
              rejectIndex >= 4
                ? {
                    x: [0, 8, -8, 6, -6, 0],
                    transition: { repeat: Infinity, duration: 3 },
                  }
                : {}
            }
            style={{ transform: `scale(${noScale})` }}
            className="w-full max-w-xs"
          >
            <AnimatedButton
              onClick={onNo}
              variant="danger"
              size="sm"
              className="w-full"
            >
              {step.noLabel || 'Still no'}
            </AnimatedButton>
          </motion.div>
        </motion.div>
      )}
    </StepCard>
  );
}

// ─── Screen: Success ──────────────────────────────────────────────────────────
function SuccessScreen({ config }) {
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  React.useEffect(() => {
    function handleResize() {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <ReactConfetti
        width={windowSize.width}
        height={windowSize.height}
        numberOfPieces={280}
        recycle={false}
        colors={['#f43f5e', '#fb7185', '#fda4af', '#c4b5fd', '#a78bfa', '#fce7f3', '#ffffff']}
        gravity={0.18}
      />
      <StepCard className="py-14">
        <motion.div
          className="text-6xl mb-4"
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 14, delay: 0.2 }}
          aria-hidden="true"
        >
          💖
        </motion.div>

        <motion.h2
          className="font-display text-4xl font-bold text-rose-600 leading-tight mb-3 text-shadow-soft"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.5 }}
        >
          This is the best.
        </motion.h2>

        <motion.p
          className="font-body text-rose-500 text-base mb-2 font-medium"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.5 }}
        >
          You&rsquo;re officially mine now. And I&rsquo;m yours.
        </motion.p>

        <motion.p
          className="font-body text-rose-400/70 text-sm mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85, duration: 0.5 }}
        >
          Thank you, {config.targetName}. Really. 🌸
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.45 }}
          className="flex flex-col gap-3 items-center w-full"
        >
          <div className="w-full max-w-xs text-center font-body text-sm text-rose-500/90 mb-2">
            You&rsquo;re officially mine now. Thank you ❤️
          </div>

          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-2 w-full max-w-xs font-body font-semibold text-white rounded-full px-7 py-3 shadow-lg shadow-rose-200 bg-gradient-to-r from-rose-400 to-petal-500 hover:from-rose-500 hover:to-petal-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2"
          >
            Start over
          </button>
        </motion.div>
      </StepCard>
    </>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const config = useConfig();

  const [mode, setMode] = useState(MODE.INTRO);
  const [introIndex, setIntroIndex] = useState(-1); // -1 = opening screen
  const [rejectIndex, setRejectIndex] = useState(0);

  const handleStart = useCallback(() => {
    setIntroIndex(0);
  }, []);

  const handleIntroBack = useCallback(() => {
    setIntroIndex((prev) => {
      if (prev <= 0) return -1; // go back to opening screen
      return prev - 1;
    });
  }, []);

  const handleIntroNext = useCallback(() => {
    setIntroIndex((prev) => {
      if (prev + 1 >= introSteps.length) {
        setMode(MODE.CONFESSION);
        return prev;
      }
      return prev + 1;
    });
  }, []);

  const handleYes = useCallback(() => {
    // track 'yes' press asynchronously without blocking UI
    trackYes({ from: config.senderName, to: config.targetName }).catch(() => {});
    setMode(MODE.SUCCESS);
  }, [config.senderName, config.targetName]);

  const handleNo = useCallback(() => {
    setMode(MODE.REJECT_LOOP);
  }, []);

  const handleRejectNo = useCallback(() => {
    setRejectIndex((prev) => {
      if (prev + 1 >= rejectSteps.length) {
        // Wrap back to last step (no true ending)
        return rejectSteps.length - 1;
      }
      return prev + 1;
    });
  }, []);

  // Resolve current intro step message
  const currentIntroStep = useMemo(() => {
    if (introIndex < 0 || introIndex >= introSteps.length) return null;
    const s = introSteps[introIndex];
    return {
      ...s,
      message:
        typeof s.message === 'function' ? s.message(config.targetName) : s.message,
    };
  }, [introIndex, config.targetName]);

  const currentRejectStep = rejectSteps[rejectIndex];

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-8 relative">
      <BackgroundDecor />

      <div className="w-full max-w-sm">
        {/* track a visit on first render (non-blocking) */}
        <TrackOnMount />
        <AnimatePresence mode="wait">
          {mode === MODE.INTRO && introIndex === -1 && (
            <motion.div key="opening">
              <OpeningScreen config={config} onStart={handleStart} />
            </motion.div>
          )}

          {mode === MODE.INTRO && introIndex >= 0 && currentIntroStep && (
            <motion.div key={`intro-${introIndex}`}>
              <IntroStepScreen
                step={currentIntroStep}
                stepIndex={introIndex}
                total={introSteps.length}
                onNext={handleIntroNext}
                onBack={handleIntroBack}
              />
            </motion.div>
          )}

          {mode === MODE.CONFESSION && (
            <motion.div key="confession">
              <ConfessionScreen config={config} onYes={handleYes} onNo={handleNo} />
            </motion.div>
          )}

          {mode === MODE.REJECT_LOOP && (
            <motion.div key={`reject-${rejectIndex}`}>
              <RejectLoopScreen
                step={currentRejectStep}
                rejectIndex={rejectIndex}
                onYes={handleYes}
                onNo={handleRejectNo}
              />
            </motion.div>
          )}

          {mode === MODE.SUCCESS && (
            <motion.div key="success">
              <SuccessScreen config={config} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

